import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";

import { getQuoteDetailsFingerprint } from "../utils/get-quote-details-fingerprint.ts";
import { useSelectedCarrier, useStepNavigation } from "./use-quote-hooks.ts";

export const useStepLogic = (quoteState: QuoteStateType) => {
	const location = useLocation();
	const { currentStep, setCurrentStep } = useStepNavigation();
	const selectedCarrier = useSelectedCarrier(quoteState);
	const isRerateRef = useRef(
		(location.state as { isRerate?: boolean } | null)?.isRerate === true,
	);

	const quoteDetailsFingerprint = useMemo(() => {
		return getQuoteDetailsFingerprint(quoteState);
	}, [quoteState]);

	const [quotedSnapshot, setQuotedSnapshot] = useState<{
		quoteId: number;
		fingerprint: string;
	} | null>(null);

	const hasQuotedRates =
		quoteState.id > 0 && quoteState.carrierPrices.length > 0;

	useEffect(() => {
		if (!hasQuotedRates) {
			if (quotedSnapshot !== null) {
				setQuotedSnapshot(null);
			}
			return;
		}

		if (isRerateRef.current) {
			if (currentStep > 1) {
				isRerateRef.current = false;
			}
			return;
		}

		const shouldCaptureInitialSnapshot =
			quotedSnapshot === null || quotedSnapshot.quoteId !== quoteState.id;
		const shouldRefreshFromQuotedSteps = currentStep > 1;

		if (!shouldCaptureInitialSnapshot && !shouldRefreshFromQuotedSteps) {
			return;
		}

		if (
			quotedSnapshot?.quoteId === quoteState.id &&
			quotedSnapshot.fingerprint === quoteDetailsFingerprint
		) {
			return;
		}

		setQuotedSnapshot({
			quoteId: quoteState.id,
			fingerprint: quoteDetailsFingerprint,
		});
	}, [
		hasQuotedRates,
		quotedSnapshot,
		quoteState.id,
		quoteDetailsFingerprint,
		currentStep,
	]);

	const quoteDetailsUnchanged =
		quotedSnapshot?.quoteId === quoteState.id &&
		quotedSnapshot.fingerprint === quoteDetailsFingerprint;
	const canRevisitCarrierSelection = hasQuotedRates && quoteDetailsUnchanged;
	const canRevisitShipmentDetails =
		canRevisitCarrierSelection && quoteState.selectedCarrierPriceId !== null;
	const disableGetQuoteButton =
		currentStep === 1 && hasQuotedRates && quoteDetailsUnchanged;

	const getStepCompleted = (step: number): boolean => {
		switch (step) {
			case 1: {
				return currentStep > 1;
			}
			case 2: {
				return (
					currentStep > 2 || (currentStep === 1 && canRevisitCarrierSelection)
				);
			}
			case 3: {
				return (
					currentStep > 3 ||
					((currentStep === 1 || currentStep === 2) &&
						canRevisitShipmentDetails)
				);
			}
			default: {
				return false;
			}
		}
	};

	return {
		currentStep,
		setCurrentStep,
		selectedCarrier,
		disableGetQuoteButton,
		getStepCompleted,
	} as const;
};

export type StepLogic = ReturnType<typeof useStepLogic>;
